import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateWebsiteBuilder } from "../../../helpers/api/crm";
import type { Customer360Snapshot } from "../../../types/crm";
import { ShowToast } from "../../../components/toast";

interface Props {
  customerId: string;
  enabled?: boolean;
}

export function WebsiteBuilderSection({ customerId, enabled }: Props) {
  const qc = useQueryClient();

  // Flag-only toggle: both enable and disable are one-click. Enabling does not
  // provision or generate anything; the builder account is created lazily at
  // first SSO.
  const toggle = useMutation({
    mutationFn: (websiteBuilderEnabled: boolean) =>
      updateWebsiteBuilder(customerId, { websiteBuilderEnabled }),
    onSuccess: (res) => {
      // Write the new flag into the cache so the button label flips immediately,
      // then invalidate the list to refetch in the background. Mirrors
      // EmailMarketingSection and avoids the invalidate-then-refetch flicker.
      qc.setQueryData<Customer360Snapshot>(
        ["crm", "customer360", customerId],
        (old) =>
          old
            ? {
                ...old,
                customer: {
                  ...old.customer,
                  websiteBuilderEnabled: res.websiteBuilderEnabled,
                },
              }
            : old,
      );
      qc.invalidateQueries({ queryKey: ["crm", "customers"] });
      ShowToast({
        message: res.websiteBuilderEnabled
          ? "Website Builder enabled"
          : "Website Builder disabled",
        type: "success",
      });
    },
    // No onError toast: the inline toggle.isError span already surfaces the
    // failure, matching EmailMarketingSection.
  });

  return (
    <section className="bg-white rounded-xl border border-border-secondary shadow-sm flex flex-col">
      <div className="px-4 py-3 border-b border-border-secondary">
        <h2 className="text-sm font-semibold text-content-primary">
          Website Builder
        </h2>
        <p className="text-[10px] text-content-tertiary mt-0.5">
          turns on the Website Builder app in the customer's sidebar
        </p>
      </div>

      <div className="flex-1 p-4 flex items-center justify-between gap-3">
        <span className="text-sm text-content-secondary">
          {enabled ? "Enabled" : "Disabled"}
        </span>
        <div className="flex items-center gap-2">
          {toggle.isError && (
            <span className="text-[11px] text-rose-600">Failed to save</span>
          )}
          {toggle.isPending && (
            <span className="text-[11px] text-content-tertiary">Saving…</span>
          )}
          {enabled ? (
            <button
              type="button"
              disabled={toggle.isPending}
              onClick={() => toggle.mutate(false)}
              className="px-3 py-1.5 text-xs font-medium text-content-secondary bg-surface-secondary rounded-md border border-border-secondary hover:bg-surface-hover transition-colors disabled:opacity-50"
            >
              Disable
            </button>
          ) : (
            <button
              type="button"
              disabled={toggle.isPending}
              onClick={() => toggle.mutate(true)}
              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              Enable
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
